import findOptionNameById from 'src/compass/shared/util/findOptionNameById';
import { DateFormat, formatDate } from 'src/compass/shared/util/casMoment';
import { filterLabel } from './renderFilter';

// Helpers for CasTable's advanced-filters drawer + chips.

// A filter is "active" when its value would narrow results. Object-shaped values
// (e.g. a date range) are active when any of their parts is set — checked without
// whitelisting key names so new range-ish shapes work unchanged.
export const isFilterActive = (value: any): boolean => {
  if (value == null || value === '') return false;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'object')
    return Object.values(value).some((v) => v != null && v !== '');
  return true;
};

// The value that clears a filter. Arrays clear to [] (the shape the multi-select
// and the backend's Count > 0 guard expect); everything else clears to null, which
// every Cas filter control treats as empty.
export const emptyFilterValue = (value: any): any => (Array.isArray(value) ? [] : null);

// A filter that declares `visibleWhen(values)` is offered only while it returns true - e.g.
// the Customers page's Is Service Owner, only while RMC has a value. The toolbar passes the
// applied values and the drawer its staged ones, so a choice made in the drawer shows or
// hides the filter before Apply.
export const isFilterShown = (filter: any, values: Record<string, any>): boolean =>
  !filter.visibleWhen || !!filter.visibleWhen(values);

// Chip date: short, since a chip sits in a row of them ("3/1/26").
const chipDate = (date: any) => formatDate(date, DateFormat.M_D_YY);

// A date-range filter's value is { start, end } (CasDateRangePicker emits 'YYYY-MM-DD'
// strings), and either end can be open.
const chipDateRange = ({ start, end }: { start?: any; end?: any }) => {
  if (start && end) return `${chipDate(start)} - ${chipDate(end)}`;
  return start ? `from ${chipDate(start)}` : `through ${chipDate(end)}`;
};

// Chip text for one applied filter. Multi-selects show a count (their option names
// live inside the component, not here); single selects resolve id -> name when the
// filter carries an options array.
export const filterChipLabel = (filter: any, value: any): string => {
  const label = filterLabel(filter);
  if (filter.type === 'date') return `${label}: ${chipDate(value)}`;
  if (filter.type === 'date-range') return `${label}: ${chipDateRange(value)}`;
  if (Array.isArray(value)) return `${label}: ${value.length} selected`;
  if (typeof value === 'boolean') return `${label}: ${value ? 'Yes' : 'No'}`;
  if (value && typeof value === 'object') return `${label}: range`;
  if (Array.isArray(filter.options)) {
    const name = findOptionNameById({
      id: value,
      options: filter.options,
      optionsIdField: filter.optionsIdField,
      optionsNameField: filter.optionsNameField,
    });
    if (name) return `${label}: ${name}`;
  }
  return `${label}: ${value}`;
};
